import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Newspaper, RefreshCw, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const HEALTHY = ['PASS', 'YES', 'reachable'];
const BROKEN = ['FAIL', 'NO', 'unavailable'];

const statusClass = (status) => {
  if (HEALTHY.includes(status)) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (BROKEN.includes(status)) return 'text-rose-700 bg-rose-50 border-rose-200';
  return 'text-amber-700 bg-amber-50 border-amber-200';
};

const SETTING_LABELS = {
  MYSHIELD_ADVERSE_MEDIA_API_URL: 'API URL',
  MYSHIELD_ADVERSE_MEDIA_HMAC_SECRET: 'HMAC secret',
  MYSHIELD_GCP_SERVICE_ACCOUNT_JSON: 'Service-account key',
};

export const MyShieldConnectionCard = () => {
  const [settings, setSettings] = useState(null);
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/adverse-media/connection`, { headers: getAuthHeaders() });
      setSettings(res.data);
    } catch (error) {
      console.error('Could not load the adverse-media connection settings');
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const runDiagnostic = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/adverse-media/connection/diagnostic`, {}, {
        headers: getAuthHeaders(), timeout: 60000,
      });
      setReport(res.data);
      if (res.data.healthy) {
        toast.success('MyShield connection verified — both authentication layers passed');
      } else {
        toast.error('MyShield connection is not working — see the reasons below');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'The diagnostic could not be run');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="border border-slate-200 shadow-sm" data-testid="myshield-connection-card">
      <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-4">
        <div className="flex items-center">
          <Newspaper className="w-5 h-5 text-slate-600 mr-2" />
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Adverse-Media Connection</h2>
            <p className="text-sm text-slate-500 mt-1">
              MyShield index, with the free index behind it as a fallback
            </p>
          </div>
        </div>
        <Button
          onClick={runDiagnostic}
          disabled={running}
          className="bg-slate-900 hover:bg-slate-800 text-white shrink-0"
          data-testid="myshield-run-diagnostic-button"
        >
          {running
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking…</>
            : <><RefreshCw className="w-4 h-4 mr-2" />Run diagnostic</>}
        </Button>
      </div>

      <div className="p-6 space-y-6">
        {settings && (
          <div data-testid="myshield-settings-presence">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Settings</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {Object.entries(settings.settings_present).map(([name, present]) => (
                <div
                  key={name}
                  className={`px-3 py-2 rounded-md border text-sm flex items-center justify-between ${present ? 'border-slate-200 bg-slate-50 text-slate-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}
                  data-testid={`myshield-setting-${name}`}
                >
                  <span>{SETTING_LABELS[name] || name}</span>
                  <span className="font-semibold">{present ? 'present' : 'empty'}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Primary source right now: <span className="font-semibold text-slate-700">{settings.provider_primary}</span>
              {' · '}fallback: {settings.fallback_provider}. {settings.note}
            </p>
          </div>
        )}

        {report ? (
          <div data-testid="myshield-diagnostic-report">
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-xs font-semibold text-slate-500 uppercase">Last diagnostic</p>
              <p className="text-xs text-slate-500 font-mono">
                {new Date(report.checked_at).toLocaleString()}
              </p>
            </div>
            <div className="divide-y divide-slate-200 border border-slate-200 rounded-md">
              {report.checks.map((check) => (
                <div key={check.key} className="px-4 py-3" data-testid={`myshield-check-${check.key}`}>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-slate-800">{check.label}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusClass(check.status)}`}>
                      {check.status}
                    </span>
                  </div>
                  {check.reason && (
                    <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{check.reason}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-600" data-testid="myshield-no-diagnostic">
            No diagnostic has been run in this session. The check makes one real authenticated
            call, so it reflects the live state rather than whether a setting is filled in.
          </p>
        )}
      </div>
    </Card>
  );
};

export default MyShieldConnectionCard;
